// src/InvoiceModal.js
import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const generateInvoiceNumber = (type, index) => {
  const d = new Date();
  const ds = `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getFullYear()).slice(-2)}`;
  const suffix = type === 'acompte' ? 'AC' : type === 'solde' ? 'SOL' : type === 'comptant' ? 'CPT' : `EC${index}`;
  return `FAC-${suffix}-${ds}${Math.floor(100 + Math.random() * 900)}`;
};

const InvoiceModal = ({ quote, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState('standard'); // standard | echelonne | comptant
  const [nbTranches, setNbTranches] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const totalTTC = quote.total_with_vat || 0;
  const acompte = totalTTC * 0.5;
  const solde = totalTTC * 0.5;
  const parTranche = solde / nbTranches;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const invoices = [];
      if (mode !== 'comptant') {

      // Toujours : facture d'acompte 50% → émise immédiatement
      invoices.push({
        quote_id: quote.id,
        user_id: user.id,
        invoice_number: generateInvoiceNumber('acompte', 0),
        type: 'acompte',
        echeance_numero: 1,
        echeance_total: mode === 'standard' ? 2 : nbTranches + 1,
        montant: acompte,
        pourcentage: 50,
        statut: 'generee',
        date_emission: new Date().toISOString().split('T')[0],
      });
      }

      if (mode === 'comptant') {
        // Facture unique 100% a la commande
        invoices.push({
          quote_id: quote.id,
          user_id: user.id,
          invoice_number: generateInvoiceNumber('comptant', 0),
          type: 'comptant',
          echeance_numero: 1,
          echeance_total: 1,
          montant: totalTTC,
          pourcentage: 100,
          statut: 'generee',
          date_emission: new Date().toISOString().split('T')[0],
        });
      } else if (mode === 'standard') {
        // Facture de solde unique
        invoices.push({
          quote_id: quote.id,
          user_id: user.id,
          invoice_number: generateInvoiceNumber('solde', 0),
          type: 'solde',
          echeance_numero: 2,
          echeance_total: 2,
          montant: solde,
          pourcentage: 50,
          statut: 'en_attente',
        });
      } else {
        // N tranches du solde
        for (let i = 1; i <= nbTranches; i++) {
          const pct = Math.round((100 / nbTranches) * 10) / 10;
          invoices.push({
            quote_id: quote.id,
            user_id: user.id,
            invoice_number: generateInvoiceNumber('echeance', i),
            type: 'echeance',
            echeance_numero: i + 1,
            echeance_total: nbTranches + 1,
            montant: i === nbTranches
              ? solde - parTranche * (nbTranches - 1) // dernière tranche = solde exact
              : parTranche,
            pourcentage: pct,
            statut: 'en_attente',
          });
        }
      }

      const { error: dbError } = await supabase
        .from('invoices')
        .insert(invoices);

      if (dbError) throw new Error(dbError.message);

      onSuccess(invoices.length);
    } catch (e) {
      setError('Erreur : ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={{
          background: 'white', borderRadius: '16px', padding: '32px',
          maxWidth: '520px', width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
          📄 Générer les factures
        </h2>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '24px' }}>
          Devis {quote.quote_number} — {fmt(totalTTC)} FCFA TTC
        </p>

        {/* Choix du mode */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>
            Mode de paiement du solde :
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '14px', borderRadius: '10px', cursor: 'pointer',
              border: `2px solid ${mode === 'standard' ? '#3b82f6' : '#e5e7eb'}`,
              background: mode === 'standard' ? '#eff6ff' : 'white'
            }}>
              <input type="radio" value="standard" checked={mode === 'standard'}
                onChange={() => setMode('standard')} style={{ marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>Standard TAKOUSSANE</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  Facture d'acompte 50% + Facture de solde 50% à la mise en service
                </div>
              </div>
            </label>

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '14px', borderRadius: '10px', cursor: 'pointer',
              border: `2px solid ${mode === 'echelonne' ? '#f59e0b' : '#e5e7eb'}`,
              background: mode === 'echelonne' ? '#fffbeb' : 'white'
            }}>
              <input type="radio" value="echelonne" checked={mode === 'echelonne'}
                onChange={() => setMode('echelonne')} style={{ marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>
                  Échelonnement exceptionnel du solde
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  Accordé à titre exceptionnel sur demande du client
                </div>
                {mode === 'echelonne' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                      Nombre de tranches du solde :
                    </label>
                    <select value={nbTranches} onChange={e => setNbTranches(Number(e.target.value))}
                      style={{ display: 'block', marginTop: '6px', width: '100%',
                        border: '1px solid #d1d5db', borderRadius: '6px',
                        padding: '8px', fontSize: '14px' }}>
                      {[2, 3, 4, 5].map(n => (
                        <option key={n} value={n}>{n} tranches</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Récapitulatif */}
        <div style={{
          background: '#f8fafc', borderRadius: '10px', padding: '16px',
          marginBottom: '24px', fontSize: '13px'
        }}>
          <p style={{ fontWeight: 700, marginBottom: '10px', color: '#111827' }}>
            Récapitulatif des factures à générer :
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Facture d'acompte (50%)</span>
              <span style={{ fontWeight: 600 }}>{fmt(acompte)} FCFA</span>
            </div>
            {mode === 'standard' ? (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Facture de solde (50%)</span>
                <span style={{ fontWeight: 600 }}>{fmt(solde)} FCFA</span>
              </div>
            ) : (
              Array.from({ length: nbTranches }, (_, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Échéance {i + 1}/{nbTranches}</span>
                  <span style={{ fontWeight: 600 }}>
                    {fmt(i === nbTranches - 1
                      ? solde - parTranche * (nbTranches - 1)
                      : parTranche)} FCFA
                  </span>
                </div>
              ))
            )}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px',
              fontWeight: 700, color: '#1e40af'
            }}>
              <span>Total</span>
              <span>{fmt(totalTTC)} FCFA</span>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: '8px', padding: '10px', color: '#ef4444',
            fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Boutons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 20px', borderRadius: '8px',
              border: '1px solid #e5e7eb', cursor: 'pointer',
              fontWeight: 500, background: 'white', fontSize: '14px' }}>
            Annuler
          </button>
          <button onClick={handleGenerate} disabled={loading}
            style={{
              padding: '10px 24px', borderRadius: '8px', border: 'none',
              background: loading ? '#93c5fd' : '#1e40af',
              color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: '14px'
            }}>
            {loading ? 'Génération...' : '✓ Générer les factures'}
          </button>

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '14px', borderRadius: '10px', cursor: 'pointer',
              border: `2px solid ${mode === 'comptant' ? '#059669' : '#e5e7eb'}`,
              background: mode === 'comptant' ? '#f0fdf4' : 'white'
            }}>
              <input type="radio" value="comptant" checked={mode === 'comptant'}
                onChange={() => setMode('comptant')} style={{ marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>100% à la commande</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  Paiement intégral — une seule facture au montant total
                </div>
              </div>
            </label>

        </div>
      </motion.div>
    </div>
  );
};

export default InvoiceModal;
